/**
 * TUVI - Formulas del Carrito de Compras
 * Administracion de coeficientes para calculo de materiales FFE
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import './FormulasCarrito.css';

const GRUPOS = {
  pisos: { label: 'Pisos', color: '#3b82f6', icon: '🏠' },
  banos: { label: 'Banos', color: '#8b5cf6', icon: '🚿' },
  pega: { label: 'Pega y Adhesivos', color: '#f59e0b', icon: '🧱' },
};

export default function FormulasCarrito() {
  const { success, error } = useNotification();
  const [formulas, setFormulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editando, setEditando] = useState(null);
  const [valorEdit, setValorEdit] = useState('');
  const [descEdit, setDescEdit] = useState('');
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const d = await client.query(`{ formulasCarrito { id clave nombre coeficiente descripcion grupo orden } }`);
      setFormulas(d.formulasCarrito || []);
    } catch { error('Error cargando formulas'); }
    finally { setLoading(false); }
  }, [error]);

  useEffect(() => { cargar(); }, [cargar]);

  const iniciarEdicion = (f) => {
    setEditando(f.id);
    setValorEdit(f.coeficiente.toString());
    setDescEdit(f.descripcion || '');
  };

  const cancelarEdicion = () => { setEditando(null); setValorEdit(''); setDescEdit(''); };

  const guardar = async (id) => {
    const coef = parseFloat(valorEdit);
    if (isNaN(coef) || coef < 0) { error('Valor invalido'); return; }
    setGuardando(true);
    try {
      const r = await client.mutate(
        `mutation($id: Int!, $coeficiente: Float!, $descripcion: String) { editarFormula(id: $id, coeficiente: $coeficiente, descripcion: $descripcion) { success message } }`,
        { id, coeficiente: coef, descripcion: descEdit }
      );
      if (r.editarFormula.success) { success('Formula actualizada'); cancelarEdicion(); cargar(); }
      else error(r.editarFormula.message);
    } catch { error('Error al guardar'); }
    finally { setGuardando(false); }
  };

  // Agrupar por grupo
  const grupos = {};
  formulas.forEach(f => {
    const g = f.grupo || 'otro';
    if (!grupos[g]) grupos[g] = [];
    grupos[g].push(f);
  });

  if (loading) return <div className="fc-loading"><div className="fc-spinner"></div><p>Cargando formulas...</p></div>;

  return (
    <div className="fc-container">
      <div className="fc-header">
        <div>
          <h2 className="fc-title">Formulas del Carrito</h2>
          <p className="fc-subtitle">Coeficientes para calculo de materiales en Ficha FFE. Los cambios aplican solo a cotizaciones futuras.</p>
        </div>
      </div>

      <div className="fc-warning">
        Los cambios en estas formulas NO afectan cotizaciones existentes. Solo aplican a cotizaciones nuevas que se calculen despues del cambio.
      </div>

      {Object.entries(grupos).map(([grupo, items]) => {
        const conf = GRUPOS[grupo] || { label: grupo, color: '#64748b', icon: '📐' };
        return (
          <div key={grupo} className="fc-grupo">
            <div className="fc-grupo-header" style={{ borderLeftColor: conf.color }}>
              <span className="fc-grupo-icon">{conf.icon}</span>
              <h3 className="fc-grupo-titulo">{conf.label}</h3>
              <span className="fc-grupo-count">{items.length} formulas</span>
            </div>
            <div className="fc-grupo-body">
              {items.map(f => (
                <div key={f.id} className={`fc-formula ${editando === f.id ? 'fc-formula-editando' : ''}`}>
                  <div className="fc-formula-info">
                    <div className="fc-formula-nombre">{f.nombre}</div>
                    <div className="fc-formula-clave"><code>{f.clave}</code></div>
                    {f.descripcion && <div className="fc-formula-desc">{f.descripcion}</div>}
                  </div>
                  {editando === f.id ? (
                    <div className="fc-formula-edit">
                      <div className="fc-edit-field">
                        <label>Coeficiente</label>
                        <input type="number" step="0.0001" value={valorEdit}
                          onChange={e => setValorEdit(e.target.value)} autoFocus
                          onKeyDown={e => { if (e.key === 'Enter') guardar(f.id); if (e.key === 'Escape') cancelarEdicion(); }} />
                      </div>
                      <div className="fc-edit-field fc-edit-desc">
                        <label>Descripcion</label>
                        <input type="text" value={descEdit} onChange={e => setDescEdit(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter') guardar(f.id); if (e.key === 'Escape') cancelarEdicion(); }} />
                      </div>
                      <div className="fc-edit-actions">
                        <button className="fc-btn fc-btn-primary" onClick={() => guardar(f.id)} disabled={guardando}>
                          {guardando ? '...' : 'Guardar'}
                        </button>
                        <button className="fc-btn fc-btn-secondary" onClick={cancelarEdicion}>Cancelar</button>
                      </div>
                    </div>
                  ) : (
                    <div className="fc-formula-valor" onClick={() => iniciarEdicion(f)}>
                      <span className="fc-valor-num">{f.coeficiente}</span>
                      <button className="fc-btn-editar" title="Editar">&#9998;</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
